import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { getWheelId } from "../../../../redux/selectors/segmentBuilderSelectors";
import { resolveAudioLink, saveRecentMedia } from "../../../../services/api";
import { getRandomId } from "../../../../utils/helpers";
import Button from "../../../Button";
import Input from "../../../Input";
import TextView from "../../../TextView";

const Container = styled.div`
  display: flex;
  margin-top: 20px;
  margin-bottom: 10px;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ErrorContainer = styled.div`
  display: flex;
  background-color: #fff4f5;
  padding: 5px;
  border-radius: 8px;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
`;

// PASTE-A-LINK copy deck (Gordon decision 2026-09-15 00:10 PT, Option A).
const PLACEHOLDER_TEXT =
  "Paste a link (Spotify, Apple Music, YouTube Music, SoundCloud, Bandcamp, Mixcloud, Deezer, Tidal, podcasts)";

const GENERIC_ERROR = "Could not read that link.";

// AudioLinkPaste (AUDIO_LINK_EMBED_ENABLED) — the Audio block's "Link" tab.
// Replaces the raw "paste iframe HTML" Embed Code tab: the user pastes a
// provider URL, we send ONLY that URL to the backend resolver
// (/api/v2/media/resolve-audio-link/) and store ONLY the URL + the resolver's
// own returned metadata on the node -- never user-pasted or provider HTML.
export default function AudioLinkPaste({ onConfirm, media }) {
  const [value, setValue] = useState(media?.originalSrc || "");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const wheelId = useSelector(getWheelId);

  const resolveUrl = async (url) => {
    if (!url) return;
    setError("");
    setLoading(true);
    try {
      const resp = await resolveAudioLink({ url });
      const data = resp?.data?.data || {};
      onConfirm({
        id: getRandomId(),
        link: true,
        provider: data.provider,
        kind: data.kind,
        title: data.title,
        author: data.author,
        image: data.thumbnail || null,
        embedUrl: data.embedUrl || null,
        src: data.audioSrc || null,
        theme: data.theme || {},
        originalSrc: url,
      });
      await saveRecentMedia(wheelId, {
        type: "3",
        url,
        metadata: {
          title: data.title,
          thumbnail: data.thumbnail,
          type: data.provider,
          link: true,
        },
      });
      setValue("");
    } catch (err) {
      setError(err?.response?.data?.message || GENERIC_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData("text");
    setValue(pastedData);
    resolveUrl(pastedData);
  };

  return (
    <Container>
      <TextView>{PLACEHOLDER_TEXT}</TextView>
      <Row>
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onPaste={handlePaste}
          variant={"secondary"}
          containerStyle={{ width: "100%", marginBottom: 0, marginRight: 10 }}
          inputStyle={{
            borderColor: error && "#B5251F",
            color: error && "#B5251F",
          }}
        />
        <Button
          isLoading={loading}
          variant="primary"
          disabled={!value || loading}
          style={{ width: 100, height: 36, fontSize: 12 }}
          onClick={() => resolveUrl(value)}
        >
          Confirm
        </Button>
      </Row>
      {error && (
        <ErrorContainer>
          <ReportProblemIcon style={{ color: "#B5251F" }} />
          <TextView variant="tag" color={"#B5251F"} style={{ fontSize: 12 }}>
            {error}
          </TextView>
        </ErrorContainer>
      )}
    </Container>
  );
}
